/**
 * EditDoctorModal — правка врача админ-сайдом
 * (PATCH /admin/clients/:id/doctors/:doctorId). Уезжает только дифф.
 */

import {useState} from 'react';
import {useMutation, useQueryClient} from '@tanstack/react-query';

import {type StaffMember, updateDoctor, type UpdateDoctorInput} from '@services/clients';
import {keys} from '@queries/keys';
import {useSpecializationOptions} from '@hooks/useFilterRefs';
import {Field, TextInput} from '@components/FormKit';
import {cn} from '@lib/utils';
import {formatKgPhone, phoneDigits} from '@utils/phone';

import {ModalShell} from '../ModalShell';

type Props = {
  open: boolean;
  clientId: string;
  doctor: StaffMember | null;
  onClose: () => void;
  onSaved: () => void;
};

export const EditDoctorModal = ({open, clientId, doctor, onClose, onSaved}: Props) => {
  const queryClient = useQueryClient();
  const specializationOptions = useSpecializationOptions();
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [specialization, setSpecialization] = useState('');

  const [prevOpen, setPrevOpen] = useState(false);
  if (open !== prevOpen) {
    setPrevOpen(open);
    if (open) {
      setFullName(doctor?.fullName ?? '');
      setPhone(phoneDigits(doctor?.phone ?? ''));
      setSpecialization(doctor?.specialization ?? '');
    }
  }

  const mutation = useMutation({
    mutationFn: updateDoctor,
    onSuccess: () => {
      void queryClient.invalidateQueries({queryKey: keys.clients.all});
      onSaved();
    },
  });

  const handleSubmit = () => {
    if (doctor === null) return;
    // Дифф: PATCH принимает только изменяемые поля
    const patch: UpdateDoctorInput = {clientId, doctorId: doctor.id};
    if (fullName.trim() !== doctor.fullName && fullName.trim().length >= 2) {
      patch.fullName = fullName;
    }
    if (phone !== phoneDigits(doctor.phone) && phoneDigits(phone).length === 9) {
      patch.phone = phone;
    }
    if (specialization !== '' && specialization !== (doctor.specialization ?? '')) {
      patch.specialization = specialization;
    }
    if (Object.keys(patch).length === 2) {
      onClose();
      return;
    }
    mutation.mutate(patch);
  };

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      title="Данные врача"
      submitLabel="Сохранить"
      onSubmit={handleSubmit}
      pending={mutation.isPending}
      submitDisabled={fullName.trim().length < 2}
      error={mutation.isError ? 'Не удалось сохранить. Попробуйте ещё раз' : null}
    >
      <Field label="ФИО врача">
        <TextInput value={fullName} onChange={(e) => setFullName(e.target.value)} />
      </Field>
      <Field label="Телефон · логин">
        <TextInput
          mono
          placeholder="700 000 000"
          value={formatKgPhone(phoneDigits(phone))}
          onChange={(e) => setPhone(phoneDigits(e.target.value))}
        />
      </Field>
      <Field label="Специализация">
        <select
          value={specialization}
          onChange={(e) => setSpecialization(e.target.value)}
          className={cn(
            'h-11 w-full appearance-none rounded-[10px] border border-input bg-card px-[13px] text-sm outline-none transition',
            'focus:border-primary focus:ring-[3px] focus:ring-primary/12',
            specialization === '' && 'text-muted-foreground',
          )}
        >
          <option value="" disabled>
            Выберите
          </option>
          {specializationOptions.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      </Field>
    </ModalShell>
  );
};
